import { useEffect, useMemo } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import { Box } from "ui/box";
import { Dialog } from "ui/dialog";
import ParentTouchpointDetails, {
  ParentTouchpointDetailsActions,
} from "components/modals/create-parent-touchpoint-modal/steps/parent-touchpoint-details";
import ParentNewCampaign, {
  ParentNewCampaignActions,
} from "components/modals/create-parent-touchpoint-modal/steps/parent-new-campaign";
import ParentTouchpointPrompt, {
  ParentTouchpointPromptActions,
} from "components/modals/create-parent-touchpoint-modal/steps/parent-touchpoint-prompt";
import ParentTouchpointPreview, {
  ParentTouchpointPreviewActions,
} from "components/modals/create-parent-touchpoint-modal/steps/parent-touchpoint-preview";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import {
  ParentTouchpointContextProvider,
  useParentTouchpointContext,
} from "components/modals/create-parent-touchpoint-modal/context/parent-touchpoint-context";
import { useCreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/hooks/use-create-parent-touchpoint-modal";

import {
  CREATE_PARENT_TOUCHPOINT_SCHEMA,
  PARENT_VALUE,
} from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal.constants";

/** The preview owns its own scroll (two side-by-side emails), so its form fills the body. */
const PREVIEW_FORM_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

interface CreateParentFormData {
  campaignId: string;
  touchpointName: string;
  campaignObjective: string;
  populationType: string;
  contentType: string;
  emailPrompt: string;
  applyClientTheme: boolean;
  folderId: string;
  insightsActivator: boolean;
  classes: string[];
  // New campaign fields
  newCampaignProgramType: string;
  newCampaignName: string;
  newCampaignCdnSubdirectoryName: string;
  newCampaignObjective: string;
  newCampaignAudiences: string[];
}

interface CreateParentTouchpointModalProps {
  setIsOpen?: (isOpen: boolean) => void;
  isOpen: boolean;
  selectedVersion?: TouchpointVersionAttributes;
}

// Main component that provides the context
export const CreateParentTouchpointModal = ({
  setIsOpen = () => {},
  isOpen,
  selectedVersion,
}: CreateParentTouchpointModalProps) => {
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  // default values for the parent touchpoint modal
  const defaultValues = useMemo(() => {
    const defaultCampaign = currentCampaign;
    const defaultCampaignName = `${defaultCampaign.name} - P`;
    const defaultParentAudiences = Array.from(
      new Set([...(defaultCampaign.audiences || []), "PARENTS"]),
    );
    const defaultCdnSubdirectoryName = defaultCampaign.cdnSubdirectoryName
      ? `parent${defaultCampaign.cdnSubdirectoryName}`
      : "";
    const defaultTouchpointName = `${selectedVersion?.name ?? currentTouchpoint.name} - P`;
    const defaultTouchpointClasses = currentTouchpoint.classes || [];

    return {
      campaignId: "", // Will be set by context when campaigns load
      touchpointName: defaultTouchpointName,
      campaignObjective: defaultCampaign.campaignObjective,
      populationType: PARENT_VALUE,
      contentType: currentTouchpoint.contentType,
      emailPrompt: "",
      applyClientTheme: selectedVersion?.applyClientTheme ?? false,
      folderId: "",
      insightsActivator: currentTouchpoint.insightsActivator,
      classes: defaultTouchpointClasses,

      // New campaign defaults
      newCampaignProgramType: defaultCampaign.programType || "ADMIT_TO_ENROLL",
      newCampaignName: defaultCampaignName,
      newCampaignCdnSubdirectoryName: defaultCdnSubdirectoryName,
      newCampaignObjective: defaultCampaign.campaignObjective || "APPLICATION_GENERATION",
      newCampaignAudiences: defaultParentAudiences,
    };
  }, [currentCampaign, currentTouchpoint, selectedVersion]);

  // form state for modal
  const methods = useForm<CreateParentFormData>({
    mode: "onChange",
    defaultValues,
    resolver: CREATE_PARENT_TOUCHPOINT_SCHEMA,
  });

  return (
    <ParentTouchpointContextProvider isOpen={isOpen} setIsOpen={setIsOpen}>
      <FormProvider {...methods}>
        <CreateParentTouchpointModalContent selectedVersion={selectedVersion} />
      </FormProvider>
    </ParentTouchpointContextProvider>
  );
};

// Inner component that uses the context
const CreateParentTouchpointModalContent = ({
  selectedVersion,
}: {
  selectedVersion?: TouchpointVersionAttributes;
}) => {
  const {
    setSelectedVersion,
    step,
    clientCampaigns,
    defaultCampaign,
    setIsOpen,
    setSelectedCampaign,
  } = useParentTouchpointContext();

  const { setValue, getValues } = useFormContext();
  const { getDefaultParentCampaignId, handleClose } = useCreateParentTouchpointModal();
  const isPreview = step === 3;

  // Ensure X, overlay, Escape, and browser back all abort any in-flight stream before closing
  const handleModalClose = (open: boolean) => {
    if (open === false) {
      handleClose();
    } else {
      setIsOpen(open);
    }
  };

  useEffect(() => {
    setSelectedVersion(selectedVersion);
  }, [selectedVersion, setSelectedVersion]);

  // Set campaignId when campaigns are loaded
  useEffect(() => {
    if (clientCampaigns.length) {
      const currentCampaignId = getValues("campaignId");
      if (!currentCampaignId) {
        const defaultParentId = getDefaultParentCampaignId(defaultCampaign, clientCampaigns);
        if (defaultParentId) {
          setValue("campaignId", defaultParentId, { shouldValidate: true });
          const selectedCampaignData = clientCampaigns.find(
            (campaign) => campaign.id === defaultParentId,
          );
          if (selectedCampaignData) {
            setSelectedCampaign(selectedCampaignData);
          }
        }
      }
    }
  }, [
    clientCampaigns,
    defaultCampaign,
    setValue,
    getValues,
    getDefaultParentCampaignId,
    setSelectedCampaign,
  ]);

  const createParentTouchpointModal = () => {
    switch (step) {
      case 1:
        return <ParentTouchpointDetails />;
      case 2:
        return <ParentTouchpointPrompt />;
      case 3:
        return <ParentTouchpointPreview />;
      case 4:
        return <ParentNewCampaign />;
    }
  };

  const renderActions = () => {
    switch (step) {
      case 2:
        return <ParentTouchpointPromptActions />;
      case 3:
        return <ParentTouchpointPreviewActions />;
      case 4:
        return <ParentNewCampaignActions />;
      default:
        return <ParentTouchpointDetailsActions />;
    }
  };

  return (
    <Dialog
      open
      onClose={() => handleModalClose(false)}
      size={isPreview ? "large" : "medium"}
      width={isPreview ? 900 : undefined}
      headline={step === 4 ? "Create parent campaign" : "Create parent email"}
      actions={renderActions()}>
      <Box component="form" sx={isPreview ? PREVIEW_FORM_SX : undefined}>
        {createParentTouchpointModal()}
      </Box>
    </Dialog>
  );
};
